import React from 'react';
import { K, HOSE as DEFAULT_HOSE, APPLIANCES as DEFAULT_APPLIANCES, inputStyle } from '../constants';
import { Btn } from '../components/ui';

const OPS_META = {
  elevFactor:         { label: 'Elevation Factor',       unit: 'psi/ft',    step: 0.01 },
  minInlet:           { label: 'Relay Min Inlet',        unit: 'psi',       step: 1 },
  standpipeFloor:     { label: 'Standpipe Floor Loss',   unit: 'psi/floor', step: 1 },
  standpipeAppliance: { label: 'Standpipe Appliance',    unit: 'psi',       step: 1 },
  defaultPumpPSI:     { label: 'Default Max Pump PSI',   unit: 'psi',       step: 5 },
  defaultPumpGPM:     { label: 'Default Pump Capacity',  unit: 'GPM',       step: 50 },
};

function NumInput({ value, onChange, step }) {
  return (
    <input
      type="number"
      min="0"
      step={step}
      value={value}
      onChange={(e) => {
        const v = parseFloat(e.target.value);
        if (!isNaN(v)) onChange(v);
      }}
      style={{ ...inputStyle(), width: 90, padding: '8px 10px', fontSize: 13, textAlign: 'center' }}
    />
  );
}

function ResetBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none', border: `1px solid ${K.bdr}`, color: K.t3,
        padding: '4px 8px', borderRadius: 8, cursor: 'pointer',
        fontFamily: K.fSans, fontSize: 10, fontWeight: 600, letterSpacing: 0.4,
      }}
    >
      Reset
    </button>
  );
}

function Row({ children, modified }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 12px', background: K.bg1,
      border: `1px solid ${modified ? K.gold : K.bdr}`,
      borderRadius: 10, marginBottom: 6,
    }}>
      {children}
    </div>
  );
}

export function CalcOverridesModal({
  adminDefaults,
  sessionOverrides,
  setSessionOverride,
  resetSessionField,
  resetAllSession,
  hasSessionOverrides,
  onClose,
}) {
  const hoseOver = sessionOverrides?.hose || {};
  const appOver  = sessionOverrides?.appliances || {};
  const opsOver  = sessionOverrides?.ops || {};

  const hoseRows = Object.keys(DEFAULT_HOSE);
  const appRows  = Object.keys(DEFAULT_APPLIANCES);
  const opsRows  = Object.keys(OPS_META);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 400, background: '#000000cc' }} />
      <div className="hc-modal-sheet" style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 401,
        background: K.bg1, borderRadius: '16px 16px 0 0', padding: 20,
        maxWidth: 560, margin: '0 auto', maxHeight: '88vh', overflowY: 'auto',
      }}>
        <div style={{ width: 40, height: 4, background: K.bdr2, borderRadius: 2, margin: '0 auto 16px' }} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ fontFamily: K.fSans, fontSize: 20, fontWeight: 700, color: K.t0 }}>Adjust Defaults</div>
          {hasSessionOverrides && (
            <button
              onClick={resetAllSession}
              style={{
                background: 'transparent', border: `1px solid ${K.warn}`, color: K.warn,
                padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                fontFamily: K.fSans, fontSize: 11, fontWeight: 600, letterSpacing: 0.3,
              }}
            >
              Reset All
            </button>
          )}
        </div>

        <div style={{
          fontFamily: K.fSans, fontSize: 11, color: K.t2, lineHeight: 1.5, marginBottom: 16,
          background: `${K.info}1a`, border: `1px solid ${K.info}44`,
          borderRadius: 10, padding: '8px 12px',
        }}>
          Tweaks here are <strong style={{ color: K.info }}>local to this device only</strong>. Other users still see the admin defaults. Tap <strong>Reset</strong> on any field to return to the admin default; tap <strong>Reset All</strong> to clear everything.
        </div>

        {/* Hose coefficients */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.red, letterSpacing: 0.3, fontWeight: 600, marginBottom: 8, paddingBottom: 4, borderBottom: `1px solid ${K.bdr}` }}>
            Hose Friction Loss (C)
          </div>
          <div style={{ fontFamily: K.fMono, fontSize: 10, color: K.t3, marginBottom: 10 }}>FL = C × (GPM/100)² × (L/100)</div>
          {hoseRows.map((key) => {
            const adminVal = adminDefaults.hose[key]?.c;
            const overVal = hoseOver[key];
            const current = overVal !== undefined ? overVal : adminVal;
            const modified = overVal !== undefined;
            const meta = DEFAULT_HOSE[key];
            const step = key.includes('5') || key.includes('4') ? 0.01 : 0.1;
            return (
              <Row key={key} modified={modified}>
                <div style={{ width: 10, height: 10, borderRadius: 5, background: meta.col, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 13, color: K.t0, fontWeight: 700 }}>{key}</span>
                  <span style={{ fontSize: 10, color: K.t3, marginLeft: 8 }}>{meta.note}</span>
                </div>
                <NumInput value={current} onChange={(v) => setSessionOverride('hose', key, v)} step={step} />
                {modified && <ResetBtn onClick={() => resetSessionField('hose', key)} />}
              </Row>
            );
          })}
        </div>

        {/* Appliance losses */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.gold, letterSpacing: 0.3, fontWeight: 600, marginBottom: 8, paddingBottom: 4, borderBottom: `1px solid ${K.bdr}` }}>
            Appliance Friction Losses
          </div>
          {appRows.map((key) => {
            const adminVal = adminDefaults.appliances[key]?.loss;
            const overVal = appOver[key];
            const current = overVal !== undefined ? overVal : adminVal;
            const modified = overVal !== undefined;
            const meta = DEFAULT_APPLIANCES[key];
            return (
              <Row key={key} modified={modified}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 12, color: K.t0, fontWeight: 600 }}>{key}</span>
                  <span style={{ fontSize: 10, color: K.t3, marginLeft: 8 }}>{meta.note}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <NumInput value={current} onChange={(v) => setSessionOverride('appliances', key, v)} step={1} />
                  <span style={{ fontSize: 10, color: K.t3 }}>psi</span>
                </div>
                {modified && <ResetBtn onClick={() => resetSessionField('appliances', key)} />}
              </Row>
            );
          })}
        </div>

        {/* Ops defaults */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.info, letterSpacing: 0.3, fontWeight: 600, marginBottom: 8, paddingBottom: 4, borderBottom: `1px solid ${K.bdr}` }}>
            Operational Defaults
          </div>
          {opsRows.map((key) => {
            const adminVal = adminDefaults.ops[key];
            const overVal = opsOver[key];
            const current = overVal !== undefined ? overVal : adminVal;
            const modified = overVal !== undefined;
            const meta = OPS_META[key];
            return (
              <Row key={key} modified={modified}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 12, color: K.t0, fontWeight: 600 }}>{meta.label}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <NumInput value={current} onChange={(v) => setSessionOverride('ops', key, v)} step={meta.step} />
                  <span style={{ fontSize: 10, color: K.t3, minWidth: 40 }}>{meta.unit}</span>
                </div>
                {modified && <ResetBtn onClick={() => resetSessionField('ops', key)} />}
              </Row>
            );
          })}
        </div>

        <Btn onClick={onClose} col={K.red} block>Done</Btn>
      </div>
    </>
  );
}
